import { useMemo, useState } from 'react';
import { PROBLEM_CATALOGUE, type ProblemTypeMetadata } from '@/api/generated-error-catalogue';
import { ERROR_CATEGORY_CATALOGUE } from '@/api/generated-error-categories';
import { ApiError } from '@/api/errors';
import { ExtractionErrorCategory, ProblemSlug } from '@/api/generated-types';
import { toastApiError } from '@/components/Notifications/apiErrorToast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTriggerServerError } from '@/services/devPanel/hooks';
import { devErrorPreviewUrl } from '@/components/DevPanel/DevErrorPreview';

function statusVariant(status: number): 'destructive' | 'warning' | 'secondary' {
  if (status >= 500) return 'destructive';
  if (status >= 400) return 'warning';
  return 'secondary';
}

// Buckets the ~40 problem codes into the families the ProblemSlug enum is itself
// organised into, so the trigger list is scannable. Derived from code + status
// rather than a per-slug map so new backend codes slot in without a FE edit.
const GROUP_ORDER = [
  'Not found',
  'Validation',
  'Conflict',
  'Auth',
  'Upstream',
  'Webhook delivery',
  'Server',
  'Other',
] as const;

function groupFor(meta: ProblemTypeMetadata): (typeof GROUP_ORDER)[number] {
  const code = String(meta.code);
  if (code.startsWith('webhook-delivery-')) return 'Webhook delivery';
  if (code === 'extraction-failed') return 'Upstream';
  if (meta.status === 500) return 'Server';
  if (meta.status === 404) return 'Not found';
  if (meta.status === 401 || meta.status === 403) return 'Auth';
  if (meta.status === 409) return 'Conflict';
  if (meta.status === 502 || meta.status === 503 || meta.status === 504) return 'Upstream';
  if (meta.status === 400 || meta.status === 422 || meta.status === 413) return 'Validation';
  return 'Other';
}

// Fabricates an ApiError and pushes it through the real toast path — no backend
// round-trip, so the trace id is synthetic. Useful for eyeballing copy quickly.
function simulateToast(status: number) {
  toastApiError(
    new ApiError({
      type: status >= 500 ? ProblemSlug.InternalServerError : ProblemSlug.ResourceNotFound,
      title: `Simulated ${status}`,
      status,
      detail: `Client-simulated ${status} error (no backend call).`,
      traceId: crypto.randomUUID().replace(/-/g, ''),
    }),
    'Simulated error'
  );
}

function simulateExtractionToast(category: ExtractionErrorCategory) {
  toastApiError(
    new ApiError({
      type: ProblemSlug.ExtractionFailed,
      title: 'Extraction failed',
      status: 422,
      detail: ERROR_CATEGORY_CATALOGUE[category].userMessage,
      errorCategory: category,
      transactionId: crypto.randomUUID(),
      traceId: crypto.randomUUID().replace(/-/g, ''),
    }),
    'Extraction failed'
  );
}

export function ErrorTriggers() {
  const trigger = useTriggerServerError();
  const [crashPanel, setCrashPanel] = useState(false);

  // Thrown during render (not in the click handler) so React's error
  // propagation kicks in — the dev panel mounts outside RouterProvider, so
  // this lands in the root ErrorBoundary.
  if (crashPanel)
    throw new Error('Simulated crash from the dev panel to preview the root ErrorBoundary.');

  // Every problem type the backend can emit, bucketed into families and ordered.
  const groups = useMemo(() => {
    const byGroup = new Map<string, ProblemTypeMetadata[]>();
    for (const meta of Object.values(PROBLEM_CATALOGUE)) {
      const key = groupFor(meta);
      const list = byGroup.get(key) ?? [];
      list.push(meta);
      byGroup.set(key, list);
    }
    for (const list of byGroup.values()) {
      list.sort((a, b) => a.status - b.status || a.code.localeCompare(b.code));
    }
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
      label: g,
      items: byGroup.get(g)!,
    }));
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-xs font-semibold">Server (real)</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Calls the backend, which throws the real exception. You get the genuine
            <span className="font-medium"> problem+json</span>, trace id and toast — copy-trace
            action appears on 5xx.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section key={group.label} className="flex flex-col gap-1.5">
              <h4 className="sticky top-0 z-10 -mx-1 bg-background/95 px-1 py-1 text-xs font-semibold text-muted-foreground backdrop-blur">
                {group.label}
                <span className="ml-1.5 font-normal opacity-60">{group.items.length}</span>
              </h4>
              <ul className="flex flex-col gap-1.5">
                {group.items.map((meta) => (
                  <li
                    key={meta.code}
                    className="flex items-center gap-2 rounded-lg border border-border/50 px-3 py-2"
                  >
                    <Badge variant={statusVariant(meta.status)} className="tabular-nums">
                      {meta.status}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate font-mono text-xs" title={meta.title}>
                      {meta.code}
                    </span>
                    <Button
                      size="xs"
                      variant="outline"
                      loading={trigger.isPending && trigger.variables === meta.code}
                      onClick={() => trigger.mutate(meta.code)}
                    >
                      Throw
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-xs font-semibold">Client sims</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            No backend call — fabricated errors pushed through the real display components. Trace
            ids are synthetic.
          </p>
        </div>
        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-muted-foreground">Toasts</h4>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="destructive" onClick={() => simulateToast(500)}>
              500 toast (w/ trace id)
            </Button>
            <Button size="sm" variant="outline" onClick={() => simulateToast(404)}>
              404 toast
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => simulateExtractionToast(ExtractionErrorCategory.DgdZeroCargo)}
            >
              422 reportable (support button)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => simulateExtractionToast(ExtractionErrorCategory.DgdPdfLowResolution)}
            >
              422 low-resolution
            </Button>
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-muted-foreground">Error boundaries</h4>
          <p className="text-xs text-muted-foreground">
            One trigger per boundary, so each fallback (and its technical-details section) can be
            previewed. Route triggers navigate with a full reload.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={() => window.location.assign(devErrorPreviewUrl('profile-api', 500))}
            >
              Profile boundary (API 500)
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => window.location.assign(devErrorPreviewUrl('profile-render'))}
            >
              Profile boundary (render crash)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.location.assign(devErrorPreviewUrl('route-loader', 500))}
            >
              Route boundary (loader 500)
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCrashPanel(true)}>
              Root boundary (crash dev panel)
            </Button>
          </div>
        </section>
      </section>
    </div>
  );
}
